import { useMemo, useState } from 'react'
import * as XLSX from 'xlsx'
import DatalogPagination from 'views/components/Datalog/DatalogPagination'

const valueAt = (row, accessorKey) => {
  if (!accessorKey) return undefined
  return String(accessorKey).split('.').reduce((value, key) => value?.[key], row)
}

const textValue = (value) => {
  if (value === null || value === undefined) return ''
  if (['string', 'number', 'boolean'].includes(typeof value)) return String(value)
  if (Array.isArray(value)) return value.map(textValue).join(' ')
  if (value?.props?.children !== undefined) return textValue(value.props.children)
  return ''
}

const columnId = (column, index) => column.id || column.accessorKey || `column-${index}`

function TableView ({
  columns = [],
  data = [],
  pageState = 0,
  pageStateSet,
  pageSizeState = 10,
  pageSizeStateSet,
  pageCount: apiPageCount = 1,
  totalRows = 0,
  manualPagination = false,
  filename,
  customFunc,
  emptyLabel = 'No data found',
  globalFilterState = '',
  globalFilterStateSet,
  searchPlaceholder = 'Cari...',
  loading = false,
  loadingLabel = 'Loading data...'
}) {
  const [sort, setSort] = useState({ id: '', direction: '' })
  const normalizedColumns = useMemo(
    () => columns.map((column, index) => ({ ...column, id: columnId(column, index) })),
    [columns]
  )

  const filteredRows = useMemo(() => {
    if (manualPagination || !globalFilterState.trim()) return data

    const needle = globalFilterState.trim().toLowerCase()
    return data.filter((row) => normalizedColumns.some((column) => {
      if (column.filterable === false || !column.accessorKey) return false
      return textValue(valueAt(row, column.accessorKey)).toLowerCase().includes(needle)
    }))
  }, [data, globalFilterState, manualPagination, normalizedColumns])

  const sortedRows = useMemo(() => {
    if (!sort.id || !sort.direction) return filteredRows

    const column = normalizedColumns.find((item) => item.id === sort.id)
    if (!column?.accessorKey) return filteredRows

    return [...filteredRows].sort((left, right) => {
      const leftValue = valueAt(left, column.accessorKey)
      const rightValue = valueAt(right, column.accessorKey)
      const comparison = typeof leftValue === 'number' && typeof rightValue === 'number'
        ? leftValue - rightValue
        : textValue(leftValue).localeCompare(textValue(rightValue), undefined, { numeric: true })
      return sort.direction === 'asc' ? comparison : -comparison
    })
  }, [filteredRows, normalizedColumns, sort])

  const pageCount = manualPagination
    ? Math.max(Number(apiPageCount) || 1, 1)
    : Math.max(Math.ceil(sortedRows.length / pageSizeState), 1)
  const visibleRows = manualPagination
    ? sortedRows
    : sortedRows.slice(pageState * pageSizeState, (pageState + 1) * pageSizeState)
  const visibleTotal = manualPagination ? Number(totalRows) || 0 : sortedRows.length

  const setPage = (nextPage) => {
    pageStateSet?.(Math.min(Math.max(nextPage, 0), pageCount - 1))
  }

  // DatalogPagination is 1-indexed (matches GlobalCallbackLogsManager's
  // currentPage/handlePageChange), while pageState here stays 0-indexed
  // internally, so we convert at the boundary.
  const handlePageChange = (nextPage) => setPage(nextPage - 1)

  const toggleSort = (column) => {
    if (column.enableSorting === false || !column.accessorKey) return

    setSort((current) => {
      if (current.id !== column.id) return { id: column.id, direction: 'asc' }
      if (current.direction === 'asc') return { id: column.id, direction: 'desc' }
      return { id: '', direction: '' }
    })
  }

  const renderCell = (row, column) => {
    const value = valueAt(row, column.accessorKey)
    if (!(column.cell instanceof Function)) return value ?? '—'

    return column.cell({
      getValue: () => value,
      row: { original: row },
      column: { columnDef: column }
    })
  }

  const exportData = () => {
    const exported = sortedRows.map((row) => Object.fromEntries(
      normalizedColumns
        .filter((column) => column.accessorKey && column.exportable !== false)
        .map((column) => [textValue(column.header) || column.accessorKey, textValue(valueAt(row, column.accessorKey))])
    ))

    if (customFunc) return customFunc(exported)

    const workbook = XLSX.utils.book_new()
    const worksheet = XLSX.utils.json_to_sheet(exported)
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Main')
    XLSX.writeFile(workbook, `${filename || 'export'}.xlsx`)
  }

  return (
    <div className="ReactTable -bordered -highlight primary-pagination">
      <div className="container-fluid my-3">
        <div className="row align-items-center g-2">
          <div className="col-12 col-md-2">
            <label className="visually-hidden" htmlFor={`${filename || 'list'}-page-size`}>Rows per page</label>
            <select
              id={`${filename || 'list'}-page-size`}
              className="form-select"
              value={pageSizeState}
              onChange={(event) => {
                pageSizeStateSet?.(Number(event.target.value))
                pageStateSet?.(0)
              }}
            >
              {[10, 25, 50, 100].map((size) => <option key={size} value={size}>{size} rows</option>)}
            </select>
          </div>

          <div className="col-12 col-md-6">
            <div className="google-search-bar">
              <i className="fa fa-search search-icon" />
              <input
                className="google-search-input"
                type="search"
                placeholder={searchPlaceholder}
                value={globalFilterState}
                onChange={(event) => globalFilterStateSet?.(event.target.value)}
              />
            </div>
          </div>

          <div className="col-12 col-md-4 text-md-end">
            <button type="button" className="btn btn-info btn-sm" onClick={exportData} disabled={!sortedRows.length}>
              <i className="fa fa-download" /> Export
            </button>
          </div>

          <div className="col-12">
            <small className="text-muted">
              Page {pageState + 1} of {pageCount} - Total {visibleTotal} Row
            </small>
          </div>
        </div>
      </div>

      <div className="table-responsive">
        <table className="rt-table table table-hover align-middle">
          <thead className="rt-thead -header">
            <tr className="rt-tr">
              {normalizedColumns.map((column) => (
                <th
                  key={column.id}
                  className="rt-th"
                  onClick={() => toggleSort(column)}
                  style={{
                    cursor: column.enableSorting === false || !column.accessorKey ? 'default' : 'pointer',
                    minWidth: column.minWidth,
                    maxWidth: column.maxWidth,
                    width: column.width
                  }}
                >
                  {column.header}
                  {column.enableSorting === false || !column.accessorKey
                    ? null
                    : <i className={`fa ms-2 ${sort.id !== column.id ? 'fa-sort' : sort.direction === 'asc' ? 'fa-sort-up' : 'fa-sort-down'}`} />}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="rt-tbody">
            {loading && !visibleRows.length
              ? (
                <tr className="rt-tr">
                  <td className="rt-td text-center py-5" colSpan={Math.max(normalizedColumns.length, 1)}>
                    <i className="fa fa-circle-o-notch fa-spin me-2" />{loadingLabel}
                  </td>
                </tr>
                )
              : visibleRows.length
                ? visibleRows.map((row, rowIndex) => (
                  <tr className="rt-tr" key={row.id ?? `${pageState}-${rowIndex}`}>
                    {normalizedColumns.map((column) => (
                      <td className="rt-td" key={`${column.id}-${row.id ?? rowIndex}`}
                        style={{
                          cursor: column.enableSorting === false || !column.accessorKey ? 'default' : 'pointer',
                          minWidth: column.minWidth,
                          maxWidth: column.maxWidth,
                          width: column.width
                        }}
                      >
                        {renderCell(row, column)}
                      </td>
                    ))}
                  </tr>
                  ))
                : (
                  <tr className="rt-tr">
                    <td className="rt-td text-center text-muted py-5" colSpan={Math.max(normalizedColumns.length, 1)}>
                      {emptyLabel}
                    </td>
                  </tr>
                  )}
          </tbody>
        </table>
      </div>

      <div className="mt-3">
        <DatalogPagination
          currentPage={pageState + 1}
          totalPage={pageCount}
          totalRecords={visibleTotal}
          pageSize={pageSizeState}
          loading={loading}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  )
}

export default TableView